import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AnimatedNumber({ value, duration = 1500, decimals = 0, suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const from = 0;
            const to = Number(value) || 0;
            const tick = (now) => {
              const elapsed = now - start;
              const progress = Math.min(1, elapsed / duration);
              const eased = 1 - Math.pow(1 - progress, 3);
              const cur = from + (to - from) * eased;
              setDisplay(cur);
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, duration]);

  const formatted = decimals
    ? display.toFixed(decimals).replace('.', ',')
    : Math.round(display).toLocaleString('fr-FR').replace(/\u202f/g, ' ');

  return (
    <span ref={ref} className="font-['Cormorant_Garamond'] text-6xl md:text-7xl lg:text-8xl font-light text-[#1A1C19] tracking-[-0.03em] leading-none">
      {formatted}
      <span className="text-[#CBA77A] ml-1">{suffix}</span>
    </span>
  );
}

export default function DomainStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/site-pulse`).then(r => setStats(r.data?.stats)).catch(() => {});
  }, []);

  if (!stats) return null;

  const items = [
    { value: stats.nights_hosted, label: 'nuitees accueillies', suffix: '' },
    { value: stats.avg_rating, label: `note moyenne · ${stats.reviews_count} avis`, suffix: ' ★', decimals: 1 },
    { value: stats.events_organized, label: 'evenements organises', suffix: '' },
    { value: stats.satisfaction_pct, label: 'taux de satisfaction', suffix: ' %' },
  ];

  return (
    <section
      data-testid="domain-stats-section"
      className="bg-[#F9F8F6] py-28 md:py-40 border-t border-b border-black/[0.04]"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="text-center mb-20 md:mb-24">
          <span className="overline">Le Pouls du Domaine</span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-6xl font-light text-[#1A1C19] mt-4 tracking-[-0.025em] leading-tight">
            Le Domaine en chiffres
          </h2>
          <p className="text-[#5A6153] text-sm md:text-base mt-5 max-w-md mx-auto font-light leading-relaxed">
            Ce que nos hotes nous ont dit, ressenti, partage.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-16 md:gap-x-12">
          {items.map((it, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="leading-none">
                <AnimatedNumber value={it.value} suffix={it.suffix} decimals={it.decimals} />
              </div>
              <p className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#5A6153] mt-5 font-medium">
                {it.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
